import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Database, ref, update } from '@angular/fire/database';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { FgsOptions } from 'src/lib/fliko/types/database.type';
import { DbQuery } from 'src/lib/fliko/types/nestedKey.type';
import { DbCollection, DbCollectionBase, DbCollectionQuery } from './dbCollection';
import { DbObjContext, DbObject } from './dbObject';

interface DbFactoryCtx {
  id: string;
  dbRef: Database;
  name: string;
}

export class DatabaseFactory {
  private objCache = new Map<string, DbObject<any>>();
  private colCache = new Map<string, DbCollectionBase<any>>();

  private ctx$ = new BehaviorSubject<DbFactoryCtx | null>(null);

  /**
   * Initialize (or reinitialize) factory
   * @param {string} contextId
   * @param dbRef
   * @param {string} name database name
   */
  public initialize(contextId: string, dbRef: Database, name: string): void {
    const oldVal = this.ctx$.value;
    if (dbRef.app.name !== oldVal?.dbRef?.app.name || contextId !== oldVal?.id) {
      this.finalize();
      this.ctx$.next({ id: contextId, dbRef, name });
    }
  }

  public finalize(): void {
    this.ctx$.next(null);

    this.objCache.clear();
    this.colCache.clear();
  }

  /** Returns the active context or throws if it is unavailable or has changed. */
  public requireContext(expectedContextId: string): DbFactoryCtx {
    const context = this.ctx$.value;
    if (!expectedContextId.trim() || !context || context.id !== expectedContextId) {
      throw new Error('Database context is unavailable or has changed');
    }
    return context;
  }

  /** Writes all paths atomically using the currently initialized database. */
  public async updatePaths(expectedContextId: string, values: Record<string, unknown>): Promise<void> {
    const context = this.requireContext(expectedContextId);
    await update(ref(context.dbRef), ObjHlp.sanitizeForDB(values));
  }

  /**
   * @param path $eId = estateId; $userId = userId, $cId = companyId
   * @return
   */
  public getObject<T>(path: string): DbObject<T> {
    return MapHlp.getSet(this.objCache, path, new DbObject<T>(this.prepareObservable(path)));
  }

  /**
   * @param path $eId = estateId; $userId = userId, $cId = companyId
   * @return
   */
  public getCollectionReadOnly<T>(path: string, query?: DbQuery): DbCollectionQuery<T> {
    const mapKey = this.prepareMapKey(path, query);
    return MapHlp.getSet(
      this.colCache,
      mapKey,
      query
        ? new DbCollectionQuery<T>(this.prepareObservable(path), query)
        : new DbCollectionBase<T>(this.prepareObservable(path))
    );
  }

  public getCollection<T>(path: string, options?: FgsOptions): DbCollection<T> {
    const mapKey = this.prepareMapKey(path);
    return MapHlp.getSet(this.colCache, mapKey, new DbCollection<T>(this.prepareObservable(path), options)) as DbCollection<T>;
  }

  private prepareMapKey(path: string, query?: DbQuery): string {
    if (query) {
      // e.g. unitShare/$eId|by:child:unitId,equalTo:XXXXXXXXXXXXXXXX
      return `${path}|${JSON.stringify(query).replace(/["{}]/g, '')}`;
    }
    return path;
  }

  // prettier-ignore
  private preparePath(path: string, id: string): string {
    if (path.includes('$userId')) path = path.replace('$userId',  id);
    if (path.includes('$eId'))    path = path.replace('$eId',     id);
    if (path.includes('$cId'))    path = path.replace('$cId',     id);

    return path;
  }

  private prepareObservable(path: string): Observable<DbObjContext | null> {
    return this.ctx$.pipe(
      map((ctx) => (ctx ? { path: this.preparePath(path, ctx.id), dbRef: ctx.dbRef, name: ctx.name } : null))
    );
  }
}
